import React from 'react'

import type { RouteGate } from './route-access'
import ForgotPassword from '@/pages/ForgotPassword'
import RedirectToDefaultRoute from './RedirectToDefaultRoute'
import { LEGACY_ADMIN_REDIRECTS, legacyRedirect } from './legacyAdminRedirects'

export interface RouteConfig extends RouteGate {
  path: string
  component: React.ComponentType
  isProtected?: boolean
  /**
   * Rota pública que gerencia a própria navegação (ex.: /auth/callback).
   * Fica fora do PublicRoute: o redirect "autenticado → /" do PublicRoute
   * correria contra o navigate() do próprio componente no instante em que
   * o login resolve.
   */
  handlesOwnAuthRedirect?: boolean
  title?: string
  /**
   * Rotas filhas para renderização aninhada (nested routes).
   */
  children?: RouteConfig[]
}

// Importações lazy para otimização
const Login = React.lazy(() => import('../pages/Login'))
const FirstAccess = React.lazy(() => import('../pages/FirstAccess'))
const Dashboard = React.lazy(() => import('../pages/Dashboard'))
const Transportadoras = React.lazy(() => import('../pages/Transportadoras'))
const AutomationFieldEditorPage = React.lazy(
  () => import('../pages/AutomationFieldEditorPage')
)
const AutomationAssociations = React.lazy(
  () => import('../pages/AutomationAssociations')
)
const CustomsNotifications = React.lazy(
  () => import('../pages/CustomsNotifications')
)
const PainelAduaneiro = React.lazy(
  () => import('../features/customs-panel/CustomsPanel')
)
const ProcessSpreadsheetPage = React.lazy(
  () => import('../features/process-spreadsheet/ProcessSpreadsheetPage')
)
const ImportPoPage = React.lazy(
  () => import('../features/purchase-orders/ImportPoPage')
)
const PoPanelPage = React.lazy(
  () => import('../features/purchase-orders/PoPanelPage')
)
const PoItemPage = React.lazy(
  () => import('../features/purchase-orders/PoItemPage')
)
const PoDetailPage = React.lazy(
  () => import('../features/purchase-orders/PoDetailPage')
)
const PoPlanilhaPage = React.lazy(
  () => import('../features/purchase-orders/planilha/PoPlanilhaPage')
)
const NovoEmbarquePage = React.lazy(
  () => import('../features/purchase-orders/embarque/NovoEmbarquePage')
)
const PoPrazosPage = React.lazy(
  () => import('../features/purchase-orders/prazos/PoPrazosPage')
)
const Macros = React.lazy(() => import('../pages/Macros'))
const PowerBI = React.lazy(() => import('../pages/PowerBI'))
const Profile = React.lazy(() => import('../pages/Profile'))
const AuthCallback = React.lazy(() => import('../pages/AuthCallback'))
const ImprovementQueue = React.lazy(() => import('../pages/ImprovementQueue'))
const NewProcess = React.lazy(
  () => import('../features/document-comparison/NewProcess')
)
const ProcessDetail = React.lazy(
  () => import('../features/process-detail/ProcessDetail')
)
const MicrosoftEmails = React.lazy(() => import('../pages/MicrosoftEmails'))
const GmailEmails = React.lazy(() => import('../pages/GmailEmails'))
const AccessDenied = React.lazy(() => import('../pages/AccessDenied'))
const Help = React.lazy(() => import('../pages/Help'))
const PostRegistry = React.lazy(() => import('../pages/PostRegistry'))
const GuiaIcmsProfiles = React.lazy(() => import('../pages/GuiaIcmsProfiles'))
const Exportacao = React.lazy(() => import('../pages/Exportacao'))
const AutomationMonitoringDetail = React.lazy(
  () => import('../pages/AutomationMonitoringDetail')
)
const ProcessTemporary = React.lazy(
  () => import('../features/process-temporary/processTemporary')
)
const Licencas = React.lazy(() => import('../pages/Licencas'))
const Tracking = React.lazy(() => import('../pages/Tracking'))
const CotacoesPositivo = React.lazy(() => import('../pages/CotacoesPositivo'))
const FreightAgentsPage = React.lazy(
  () => import('../pages/FreightAgents/FreightAgentsPage')
)
const QuotationsListPage = React.lazy(
  () => import('../pages/Quotations/QuotationsListPage')
)
const QuotationCreatePage = React.lazy(
  () => import('../pages/Quotations/QuotationCreatePage')
)
const QuotationSettingsPage = React.lazy(
  () => import('../pages/Quotations/QuotationSettingsPage')
)
const QuotationSavingsPage = React.lazy(
  () => import('../pages/Quotations/QuotationSavingsPage')
)
const QuotationDetailPage = React.lazy(
  () => import('../pages/Quotations/QuotationDetailPage')
)
const QuotationComparisonPage = React.lazy(
  () => import('../pages/Quotations/QuotationComparisonPage')
)
const PublicQuotationResponsePage = React.lazy(
  () => import('../pages/PublicQuotation/PublicQuotationResponsePage')
)
const TrackingDetail = React.lazy(() => import('../pages/TrackingDetail'))
const AdminPanel = React.lazy(() => import('../features/admin/AdminPanel'))
const VisaoGeralView = React.lazy(() => import('../pages/Admin/VisaoGeralView'))
const UsersView = React.lazy(() => import('../pages/Admin/UsersView'))
const EquipeView = React.lazy(() => import('../pages/Equipe/EquipeView'))
const AgentsView = React.lazy(() => import('../pages/Admin/AgentsView'))
const ProcessTypesView = React.lazy(
  () => import('../pages/Admin/ProcessTypesView')
)
const OnboardingWizard = React.lazy(
  () => import('../features/admin/onboarding/OnboardingWizard')
)
const CompaniesListView = React.lazy(
  () => import('../pages/Admin/CompaniesListView')
)
const CompanyDetailView = React.lazy(
  () => import('../pages/Admin/CompanyDetailView')
)
const ProcessTypeDetailView = React.lazy(
  () => import('../pages/Admin/ProcessTypeDetailView')
)
const ModeloMapView = React.lazy(() => import('../pages/Admin/ModeloMapView'))
const AutomacoesGroup = React.lazy(() => import('../features/admin/groups/AutomacoesGroup'))
const MicrosservicosGroup = React.lazy(() => import('../features/admin/groups/MicrosservicosGroup'))
const FeaturesGroup = React.lazy(() => import('../features/admin/groups/FeaturesGroup'))
const MonitoramentoGroup = React.lazy(() => import('../features/admin/groups/MonitoramentoGroup'))
const AdminAverbacaoTerminal = React.lazy(() => import('../features/admin/averbacao/TerminalEditView'))
const AdminVeloster = React.lazy(() => import('../features/admin/sistema-sections/VelosterSection'))
const AdminDocs = React.lazy(() => import('../features/admin/sistema-sections/DocsSection'))
const AdminHojeTemDeploy = React.lazy(() => import('../features/admin/sistema-sections/HojeTemDeploySection'))
const PlannerRedirect = React.lazy(() =>
  import('../features/planner/pages/PlannerRedirect').then((m) => ({
    default: m.PlannerRedirect,
  }))
)
const PlannerMyTasks = React.lazy(() =>
  import('../features/planner/pages/MyTasksPage').then((m) => ({
    default: m.MyTasksPage,
  }))
)
const PlannerProjects = React.lazy(() =>
  import('../features/planner/pages/ProjectsPage').then((m) => ({
    default: m.ProjectsPage,
  }))
)
const PlannerProjectDetail = React.lazy(() =>
  import('../features/planner/pages/ProjectDetailPage').then((m) => ({
    default: m.ProjectDetailPage,
  }))
)
const PlannerPipelineDetail = React.lazy(() =>
  import('../features/planner/pages/PipelinePage').then((m) => ({
    default: m.PipelinePage,
  }))
)
const PlannerDashboard = React.lazy(() =>
  import('../features/planner/pages/DashboardPage').then((m) => ({
    default: m.DashboardPage,
  }))
)
const PlannerEmployeeTasks = React.lazy(() =>
  import('../features/planner/pages/EmployeeTasksPage').then((m) => ({
    default: m.EmployeeTasksPage,
  }))
)
const CatalogComparison = React.lazy(
  () => import('../features/catalog-comparison/CatalogComparisonPage')
)
const SaldoConsultation = React.lazy(
  () => import('../features/saldo-consultation/SaldoConsultationPage')
)
const ProcessInstructions = React.lazy(
  () => import('../pages/ProcessInstructions')
)
const FinancialPanel = React.lazy(() => import('../pages/FinancialPanel'))
const FinancialReports = React.lazy(() => import('../pages/FinancialReports'))
const OnboardingRecordsPage = React.lazy(
  () => import('../features/onboarding-records/OnboardingRecordsPage')
)

const CeCct = React.lazy(
  () => import('../features/ce-cct/CeCct')
)
const Demurrage = React.lazy(
  () => import('../features/demurrage/Demurrage')
)
const ImportSimulationPage = React.lazy(
  () => import('../pages/ImportSimulation/ImportSimulationPage')
)
const PaineisLauncher = React.lazy(
  () => import('../pages/paineis/PaineisLauncher')
)

// Placeholder para páginas futuras
const PlaceholderPage = React.lazy(
  () => import('../components/PlaceholderPage')
)

export const routes: RouteConfig[] = [
  // Rota raiz - redireciona para a primeira tela acessível de LANDING_ORDER
  // (painel aduaneiro -> dashboard -> PowerBI -> perfil).
  {
    path: '/',
    component: RedirectToDefaultRoute,
    isProtected: true,
    allowReadOnly: true,
    title: 'Início',
  },
  {
    path: '/login',
    component: Login,
    isProtected: false,
    title: 'Login',
  },
  {
    path: '/first-access',
    component: FirstAccess,
    isProtected: false,
    title: 'Primeiro Acesso',
  },
  {
    path: '/forgot-password',
    component: ForgotPassword,
    isProtected: false,
    title: 'Recuperar Senha',
  },
  {
    path: '/auth/callback',
    component: AuthCallback,
    isProtected: false,
    handlesOwnAuthRedirect: true,
    title: 'Autenticando',
  },
  {
    path: '/access-denied',
    component: AccessDenied,
    isProtected: true,
    allowReadOnly: true,
    title: 'Acesso Negado',
  },

  // Rotas protegidas
  {
    path: '/equipe',
    component: EquipeView,
    isProtected: true,
    allowReadOnly: true,
    title: 'Equipe',
  },
  {
    path: '/dashboard',
    component: Dashboard,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Dashboard',
    requiredFeatures: 'automacoes',
  },
  {
    path: '/automacoes',
    component: Dashboard,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Dashboard',
    requiredFeatures: 'automacoes',
  },
  {
    path: '/automation-associations',
    component: AutomationAssociations,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Associar Automações',
  },
  {
    path: '/fila-melhorias',
    component: ImprovementQueue,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Fila de Solicitações',
    requiredPermissions: { action: 'read', subject: 'Solicitacao' },
    requiredFeatures: 'solicitacoes',
  },
  {
    path: '/adicionar-automacao',
    component: PlaceholderPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Adicionar Automação',
  },
  {
    path: '/notificacoes-aduaneiras',
    component: CustomsNotifications,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Notificações Aduaneiras',
    requiredFeatures: 'notificacoes-aduaneiras',
  },
  {
    path: '/conhecimentos-embarque',
    component: PlaceholderPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Conhecimentos de Embarque',
  },
  {
    path: '/cargas-aereos',
    component: PlaceholderPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Cargas Aéreas',
  },
  {
    path: '/painel-aduaneiro',
    component: PainelAduaneiro,
    isProtected: true,
    requiredCaps: 'can_view_painel',
    allowReadOnly: true,
    title: 'Painel de Controle',
    requiredFeatures: 'painel-aduaneiro',
  },
  {
    path: '/planilha-processos',
    component: ProcessSpreadsheetPage,
    isProtected: true,
    requiredCaps: 'can_view_painel',
    allowReadOnly: true,
    title: 'Planilha de Processos',
    requiredFeatures: 'painel-aduaneiro',
  },
  {
    path: '/importar-po',
    component: ImportPoPage,
    isProtected: true,
    title: 'Importar Purchase Orders',
    requiredPermissions: { action: 'create', subject: 'PurchaseOrder' },
    requiredCaps: 'can_create_processes',
    requiredFeatures: 'purchase-orders',
  },
  {
    path: '/purchase-orders',
    component: PoPanelPage,
    isProtected: true,
    title: 'Purchase Orders',
    requiredPermissions: { action: 'read', subject: 'PurchaseOrder' },
    requiredCaps: 'can_create_processes',
    requiredFeatures: 'purchase-orders',
  },
  {
    path: '/purchase-orders/item',
    component: PoItemPage,
    isProtected: true,
    title: 'Item',
    requiredPermissions: { action: 'read', subject: 'PurchaseOrder' },
    requiredCaps: 'can_create_processes',
    requiredFeatures: 'purchase-orders',
  },
  {
    path: '/purchase-orders/planilha',
    component: PoPlanilhaPage,
    isProtected: true,
    title: 'Planilha de POs',
    requiredPermissions: { action: 'read', subject: 'PurchaseOrder' },
    requiredCaps: 'can_create_processes',
    requiredFeatures: 'purchase-orders',
  },
  {
    path: '/purchase-orders/prazos',
    component: PoPrazosPage,
    isProtected: true,
    title: 'Prazos',
    requiredPermissions: { action: 'read', subject: 'PurchaseOrder' },
    requiredCaps: 'can_create_processes',
    requiredFeatures: 'purchase-orders',
  },
  {
    path: '/purchase-orders/embarques/novo',
    component: NovoEmbarquePage,
    isProtected: true,
    title: 'Emitir Pré-alerta',
    requiredPermissions: { action: 'create', subject: 'PurchaseOrder' },
    requiredCaps: 'can_create_processes',
    requiredFeatures: 'purchase-orders',
  },
  // Depois das rotas fixas de /purchase-orders.
  {
    path: '/purchase-orders/:poId',
    component: PoDetailPage,
    isProtected: true,
    title: 'Purchase Order',
    requiredPermissions: { action: 'read', subject: 'PurchaseOrder' },
    requiredCaps: 'can_create_processes',
    requiredFeatures: 'purchase-orders',
  },
  {
    path: '/ce-cct',
    component: CeCct,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'CE-CCT',
    requiredFeatures: 'ce-cct',
  },
  {
    path: '/demurrage',
    component: Demurrage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Demurrage',
    requiredFeatures: 'demurrage',
  },
  {
    path: '/pos-registro',
    component: PostRegistry,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Pós-Registro',
    requiredFeatures: 'pos-registro',
  },
  {
    path: '/transportadoras',
    component: Transportadoras,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Transportadoras',
    /**
     * Sem `allowedRoles`: a feature `transportadoras` é `rankConfigurable`, e o
     * backend já resolve o nível mínimo por CC dentro de `enabled_features`.
     * Espelhar a lista aqui só criaria drift — e obrigaria a mexer em código
     * para liberar a tela a mais um cargo.
     */
    requiredFeatures: 'transportadoras',
  },
  {
    path: '/admissoes-temporarias',
    component: ProcessTemporary,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Admissões Temporárias',
    requiredFeatures: 'admissoes-temporarias',
  },
  {
    path: '/guia-icms',
    component: GuiaIcmsProfiles,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Guia ICMS',
    requiredFeatures: 'guia-icms',
  },
  {
    path: '/macros',
    component: Macros,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Macros',
    requiredFeatures: 'macros',
  },
  {
    path: '/powerbi',
    component: PowerBI,
    isProtected: true,
    allowReadOnly: true,
    title: 'Power BI',
    requiredCapabilities: 'canViewPowerBI',
  },
  {
    path: '/perfil',
    component: Profile,
    isProtected: true,
    allowReadOnly: true,
    title: 'Meu Perfil',
  },
  {
    path: '/comparacao-documentos',
    component: NewProcess,
    isProtected: true,
    requiredCaps: 'can_create_processes',
    allowReadOnly: true,
    title: 'Novo Processo',
    requiredPermissions: { action: 'read', subject: 'Process' },
    requiredFeatures: 'comparacao-documentos',
  },
  {
    path: '/processo/:processId',
    component: ProcessDetail,
    isProtected: true,
    requiredCaps: 'can_view_processes',
    allowReadOnly: true,
    title: 'Detalhes do Processo',
  },
  {
    path: '/microsoft-emails',
    component: MicrosoftEmails,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Microsoft Emails',
  },
  {
    path: '/gmail-emails',
    component: GmailEmails,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Gmail Emails',
  },
  // {
  //   path: '/kitai-chatbot',
  //   component: PlaceholderPage,
  //   isProtected: true,
  //   title: 'KitAI Chatbot'
  // },
  {
    path: '/cotfy-cotacoes',
    component: PlaceholderPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Cotfy Cotações',
  },
  {
    path: '/ajuda',
    component: Help,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Ajuda',
  },
  {
    path: '/exportacao',
    component: Exportacao,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Exportação',
    requiredFeatures: 'exportacao',
  },
  {
    path: '/cotacoes-positivo',
    component: CotacoesPositivo,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Cotações Positivo',
    requiredFeatures: 'cotacoes-positivo',
  },
  {
    path: '/agentes-carga',
    component: FreightAgentsPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Agentes de Carga',
  },
  {
    path: '/cotacoes',
    component: QuotationsListPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Cotações',
  },
  {
    path: '/cotacoes/nova',
    component: QuotationCreatePage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Nova Cotação',
  },
  {
    path: '/cotacoes/configuracoes',
    component: QuotationSettingsPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Configurações de Cotação',
  },
  {
    path: '/cotacoes/saving',
    component: QuotationSavingsPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Saving das Cotações',
  },
  {
    path: '/cotacoes/:id',
    component: QuotationDetailPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Detalhe da Cotação',
  },
  {
    path: '/cotacoes/:id/comparacao',
    component: QuotationComparisonPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Comparação de Propostas',
  },
  {
    path: '/q/:token',
    component: PublicQuotationResponsePage,
    isProtected: false,
    title: 'Responder Cotação',
  },
  {
    path: '/simulacoes-importacao',
    component: ImportSimulationPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Simulação de Importação',
    requiredFeatures: 'simulacao-importacao',
    requiredPermissions: { action: 'read', subject: 'ImportSimulation' },
  },
  {
    path: '/simulacoes-importacao/:id',
    component: ImportSimulationPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Simulação de Importação',
    requiredFeatures: 'simulacao-importacao',
    requiredPermissions: { action: 'read', subject: 'ImportSimulation' },
  },
  {
    path: '/licencas',
    component: Licencas,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Licenças',
    requiredPermissions: { action: 'read', subject: 'Catalogo' },
    requiredFeatures: 'licencas',
  },
  {
    path: '/tracking',
    component: Tracking,
    isProtected: true,
    requiredCaps: 'can_view_tracking',
    allowReadOnly: true,
    title: 'Tracking',
    requiredPermissions: { action: 'read', subject: 'Process' },
  },
  {
    path: '/admin',
    component: AdminPanel,
    isProtected: true,
    title: 'Painel Admin',
    requiredCapabilities: 'canManageDevTools',
    children: [
      { path: '/admin', component: VisaoGeralView, isProtected: true, title: 'Visão Geral', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/automacoes/:tab?', component: AutomacoesGroup, isProtected: true, title: 'Automações', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/automacoes/campos/:id?', component: AutomationFieldEditorPage, isProtected: true, title: 'Editor de Campos', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/automacoes/execucoes/:id', component: AutomationMonitoringDetail, isProtected: true, title: 'Detalhes da Execução', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/microsservicos/:tab?', component: MicrosservicosGroup, isProtected: true, title: 'Microsserviços', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/features/:tab?', component: FeaturesGroup, isProtected: true, title: 'Features', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/features/averbacao/terminais/:id', component: AdminAverbacaoTerminal, isProtected: true, title: 'Terminal de Averbação', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/monitoramento/:tab?', component: MonitoramentoGroup, isProtected: true, title: 'Monitoramento', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/veloster', component: AdminVeloster, isProtected: true, title: 'Veloster', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/docs', component: AdminDocs, isProtected: true, title: 'Documentação' },
      { path: '/admin/hoje-tem-deploy', component: AdminHojeTemDeploy, isProtected: true, title: 'Hoje tem deploy?', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/empresas', component: CompaniesListView, isProtected: true, title: 'Empresas', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/empresas/:id', component: CompanyDetailView, isProtected: true, title: 'Detalhe da Empresa', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/usuarios/:tab?', component: UsersView, isProtected: true, title: 'Usuários e Autenticação', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/agentes', component: AgentsView, isProtected: true, title: 'Agentes', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/tipos-processo', component: ProcessTypesView, isProtected: true, title: 'Tipos de Processo', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/tipos-processo/:id', component: ProcessTypeDetailView, isProtected: true, title: 'Detalhes do Tipo de Processo', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/modelo', component: ModeloMapView, isProtected: true, title: 'Mapa do modelo', requiredCapabilities: 'canManageDevTools' },
      { path: '/admin/onboarding', component: OnboardingWizard, isProtected: true, title: 'Onboardar empresa', requiredCapabilities: 'canManageDevTools' },
      // Subcaminho desconhecido volta pra Visão Geral (splat fica abaixo de toda rota real).
      { path: '/admin/*', component: legacyRedirect('/admin'), isProtected: true, title: 'Redirecionando', requiredCapabilities: 'canManageDevTools' },
    ],
  },
  // Rotas antigas do admin e das telas que viviam fora dele → endereço novo.
  ...LEGACY_ADMIN_REDIRECTS.map((r): RouteConfig => ({
    path: r.from,
    component: legacyRedirect(r.to),
    isProtected: true,
    title: 'Redirecionando',
    requiredCapabilities: 'canManageDevTools',
  })),
  {
    path: '/tracking/:id',
    component: TrackingDetail,
    isProtected: true,
    requiredCaps: 'can_view_tracking',
    allowReadOnly: true,
    title: 'Detalhes do Tracking',
    requiredPermissions: { action: 'read', subject: 'Process' },
  },
  // O grupo /planner inteiro tinha `allowedRoles: ['Desenvolvedor', 'Presidente']`
  // redundante com `requiredCapabilities: 'canAccessPlanner'` — removido
  // (não substituído por minRank/requiredCaps): `canAccessPlanner` já é
  // `ability.can(READ, 'Planner')`, que no backend só é true pra
  // Desenvolvedor/Presidente (TYPE_SEEDS.presidente/desenvolvedor têm
  // `can_access_planner`; Agente tem `can_manage_all` mas NÃO tem
  // `can_access_planner`, e a restrição em ability.factory.ts explicitamente
  // faz `cannot(MANAGE/READ, 'Planner')` nesse caso). Duas fontes pro mesmo
  // gate divergiriam cedo ou tarde — uma só, já correta, basta.
  {
    path: '/planner',
    component: PlannerRedirect,
    isProtected: true,
    title: 'Planner',
    requiredCapabilities: 'canAccessPlanner',
  },
  {
    path: '/planner/dashboard',
    component: PlannerDashboard,
    isProtected: true,
    title: 'Dashboard',
    requiredCapabilities: 'canAccessPlanner',
  },
  {
    path: '/planner/my',
    component: PlannerMyTasks,
    isProtected: true,
    title: 'Minhas tasks',
    requiredCapabilities: 'canAccessPlanner',
  },
  {
    path: '/planner/projects',
    component: PlannerProjects,
    isProtected: true,
    title: 'Projetos',
    requiredCapabilities: 'canAccessPlanner',
  },
  {
    path: '/planner/projects/:projectId',
    component: PlannerProjectDetail,
    isProtected: true,
    title: 'Projeto',
    requiredCapabilities: 'canAccessPlanner',
  },
  {
    path: '/planner/pipelines/:projectId',
    component: PlannerPipelineDetail,
    isProtected: true,
    title: 'Pipeline',
    requiredCapabilities: 'canAccessPlanner',
  },
  {
    path: '/planner/my/tasks/:taskId',
    component: PlannerMyTasks,
    isProtected: true,
    title: 'Minhas tasks',
    requiredCapabilities: 'canAccessPlanner',
  },
  {
    path: '/planner/employees/:employeeId/tasks',
    component: PlannerEmployeeTasks,
    isProtected: true,
    title: 'Tasks do colaborador',
    requiredCapabilities: 'canAccessPlanner',
  },
  {
    path: '/planner/employees/:employeeId/tasks/:taskId',
    component: PlannerEmployeeTasks,
    isProtected: true,
    title: 'Tasks do colaborador',
    requiredCapabilities: 'canAccessPlanner',
  },
  {
    path: '/planner/projects/:projectId/tasks/:taskId',
    component: PlannerProjectDetail,
    isProtected: true,
    title: 'Projeto',
    requiredCapabilities: 'canAccessPlanner',
  },
  {
    path: '/comparacao-catalogo',
    component: CatalogComparison,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Comparação de Catálogo',
    requiredFeatures: 'comparacao-catalogo',
  },
  {
    path: '/consultar-saldos',
    component: SaldoConsultation,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Consultar Saldos',
    requiredFeatures: 'consultar-saldos',
  },
  {
    path: '/instrucoes-processo',
    component: ProcessInstructions,
    isProtected: true,
    requiredCaps: 'can_view_instructions',
    allowReadOnly: true,
    title: 'Instruções de Processo',
    requiredFeatures: 'instrucoes-processo',
  },
  {
    path: '/painel-financeiro',
    component: FinancialPanel,
    isProtected: true,
    requiredCaps: 'can_access_financial_panel',
    title: 'Painel Financeiro',
    requiredFeatures: 'painel-financeiro',
  },
  {
    path: '/relatorios-financeiros',
    component: FinancialReports,
    isProtected: true,
    requiredCaps: 'can_access_financial_panel',
    title: 'Relatórios Financeiros',
    requiredFeatures: 'painel-financeiro.relatorios',
  },
  {
    path: '/paineis/launch',
    component: PaineisLauncher,
    isProtected: true,
    requiredCaps: 'can_view_paineis',
    allowReadOnly: true,
    title: 'Dashboards',
  },
  {
    path: '/registro-onboarding',
    component: OnboardingRecordsPage,
    isProtected: true,
    requiredCaps: 'can_view_core',
    title: 'Registro de Onboarding',
    // Espelha assertSupervisor/isAtLeastSupervisor do
    // OnboardingRecordsController (backend) — hierárquico, fail-closed.
    minRank: 'supervisor',
  },
]

/**
 * Rotas protegidas abertas a qualquer autenticado. `/equipe`: o backend recorta
 * por empresas em comum, então a página não precisa de capability.
 */
export const OPEN_PATHS = ['/', '/perfil', '/access-denied', '/equipe'] as const

/** /occurrences mora no AppRouter (layout próprio), não em `routes`. */
export const OCCURRENCES_GATE: RouteGate = {
  requiredCapabilities: 'canAccessOccurrences',
  requiredFeatures: 'occurrences',
  allowReadOnly: true,
}

/** Primeira acessível vira a tela inicial. /paineis/launch fica fora: sai do hub. */
export const LANDING_ORDER = ['/painel-aduaneiro', '/dashboard', '/powerbi', '/perfil'] as const

export function gateForPath(path: string): RouteGate | undefined {
  if (path === '/occurrences') return OCCURRENCES_GATE
  return routes.find((r) => r.path === path)
}
